<template>
  <div class="card-tile" :class="{ excluded }">
    <div
      ref="stage"
      class="tile-stage"
      :class="{ landscape: isLandscape }"
      role="button"
      :title="clickHint"
      @click="$emit('open', card)"
    >
      <img v-if="svgUri" :src="svgUri" class="tile-img" :alt="card.name" />
      <div v-else-if="failed" class="tile-failed">
        <div class="small">Preview unavailable</div>
        <button class="btn btn-sm btn-link p-0 small" @click.stop="retry">Retry</button>
      </div>
      <div v-else class="tile-skeleton"></div>

      <div v-if="card.repeats > 1" class="tile-badge">×{{ card.repeats }}</div>
      <div v-if="excluded" class="tile-excluded-flag">Not printed</div>
    </div>

    <div class="tile-meta">
      <div class="tile-name" :title="card.name">{{ card.name }}</div>
      <div v-if="card.type || card.subtype" class="tile-type">
        {{ [card.type, card.subtype].filter(Boolean).join(' · ') }}
      </div>
    </div>

    <div class="tile-actions">
      <button class="btn btn-sm btn-outline-secondary" @click.stop="$emit('open', card)">
        {{ canEdit ? 'Edit' : 'View' }}
      </button>
      <button
        class="btn btn-sm"
        :class="excluded ? 'btn-outline-success' : 'btn-outline-danger'"
        @click.stop="$emit('toggle-print', card)"
      >
        {{ excluded ? 'Include' : 'Exclude' }}
      </button>
      <button
        v-if="card.parent_id"
        class="btn btn-sm btn-outline-primary"
        @click.stop="$emit('variants', card)"
      >
        Variants
      </button>
    </div>
  </div>
</template>

<script>
import { sparkforgeApi, svgToDataUri } from '@/sparkforgeApi';

export default {
  name: 'CardTile',
  props: {
    gameId: { type: String, required: true },
    card: { type: Object, required: true },
    canEdit: { type: Boolean, default: true },
    excluded: { type: Boolean, default: false },
    // Bumped by the parent when the deck's design changes, to force a re-render.
    designVersion: { type: Number, default: 0 },
    // Position in the grid, used to stagger the fallback render.
    index: { type: Number, default: 0 }
  },
  emits: ['open', 'toggle-print', 'variants'],
  data() {
    return {
      svgUri: null,
      isLandscape: false,
      observer: null,
      rendered: false,
      failed: false,
      fallbackTimer: null
    };
  },
  computed: {
    clickHint() {
      return this.canEdit ? 'Click to edit' : 'Click to view';
    }
  },
  watch: {
    designVersion() {
      this.rendered = false;
      this.render();
    },
    'card.description'() {
      this.rendered = false;
      this.render();
    },
    'card.name'() {
      this.rendered = false;
      this.render();
    }
  },
  mounted() {
    // Prefer rendering when the tile nears the viewport — a 45-card deck should
    // not fire 45 requests for cards nobody has scrolled to.
    if ('IntersectionObserver' in window && this.$refs.stage) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) this.render();
        },
        { rootMargin: '300px' }
      );
      this.observer.observe(this.$refs.stage);
    }

    // Fallback. IntersectionObserver does not fire in every context (some
    // embedded and automation environments never deliver the callback), and a
    // grid of permanent skeletons is a far worse failure than a few extra
    // requests. The stagger keeps a large deck from stampeding the API.
    this.fallbackTimer = setTimeout(this.render, 1200 + this.index * 120);
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.fallbackTimer);
  },
  methods: {
    retry() {
      this.failed = false;
      this.rendered = false;
      this.render();
    },
    async render() {
      if (this.rendered || !this.gameId) return;
      clearTimeout(this.fallbackTimer);
      if (this.observer) this.observer.disconnect();
      this.rendered = true;
      try {
        const result = await sparkforgeApi.previewCard(this.gameId, this.card);
        this.svgUri = svgToDataUri(result.svg);
        const m = result.svg.match(/width="(\d+)"[\s\S]*?height="(\d+)"/);
        this.isLandscape = m ? Number(m[1]) > Number(m[2]) : false;
      } catch (e) {
        // Show the failure rather than an eternal shimmer.
        this.failed = true;
        this.rendered = false; // allow a retry on the next design change
      }
    }
  }
};
</script>

<style scoped>
.card-tile {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tile-stage {
  position: relative;
  aspect-ratio: 180 / 252;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: var(--bg-surface, #fff);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.tile-stage.landscape { aspect-ratio: 252 / 180; }
.tile-stage:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.16);
}

.tile-img { width: 100%; height: 100%; object-fit: contain; display: block; }

.tile-failed {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  color: var(--ink-light, #8a8a8a);
  border: 1px dashed var(--border-sketch, #d5cfc4);
  border-radius: 10px;
}

.tile-skeleton {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f3f0ea 25%, #e9e5dd 37%, #f3f0ea 63%);
  background-size: 400% 100%;
  animation: tile-shimmer 1.4s ease-in-out infinite;
}
@keyframes tile-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.tile-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(28, 28, 28, 0.82);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
}

.tile-excluded-flag {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(157, 51, 44, 0.9);
  color: #fff;
  font-size: 0.66rem;
  text-align: center;
  padding: 2px 0;
}

.card-tile.excluded .tile-stage { opacity: 0.55; }

.tile-meta { min-height: 2.1rem; }
.tile-name {
  font-weight: 600;
  font-size: 0.86rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tile-type {
  font-size: 0.72rem;
  color: var(--ink-light, #7a7a7a);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tile-actions { display: flex; gap: 0.3rem; flex-wrap: wrap; }
.tile-actions .btn { font-size: 0.72rem; padding: 0.15rem 0.5rem; }
</style>
